import React from 'react';
import { Info, Loader2, Save, CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { formatDateTimeLocal } from '../../../utils/formatters';

export const InfoTip = ({ text, testId }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label="More information" data-testid={testId}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-200">
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">{text}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const StatusDot = ({ tone = 'slate', children, testId }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 [&>i]:bg-emerald-500',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 [&>i]:bg-amber-500',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 [&>i]:bg-blue-500',
    slate: 'bg-slate-50 text-slate-600 border-slate-200 [&>i]:bg-slate-400',
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${tones[tone]}`}>
      <i className="h-1.5 w-1.5 rounded-full" />{children}
    </span>
  );
};

export const CheckChip = ({ children, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
    <CheckCircle2 className="h-3 w-3" />{children}
  </span>
);

export const PanelHeader = ({ icon: Icon, iconCls, title, subtitle, right, testId }) => (
  <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 md:flex-row md:items-center" data-testid={testId}>
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconCls}`}><Icon className="h-5 w-5" /></div>
    <div className="min-w-0 flex-1">
      <h2 className="text-[15px] font-semibold leading-tight text-slate-900">{title}</h2>
      <p className="mt-0.5 text-[13px] text-slate-500">{subtitle}</p>
    </div>
    {right && <div className="flex shrink-0 items-center gap-4 md:pl-4">{right}</div>}
  </div>
);

export const AutomationToggle = ({ label = 'Automation', checked, children }) => (
  <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-1.5">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <span className={`w-7 text-[11px] font-semibold ${checked ? 'text-emerald-600' : 'text-slate-400'}`}>{checked ? 'ON' : 'OFF'}</span>
    {children}
  </div>
);

export const SettingsSection = ({ title, tip, children, testId }) => (
  <section className="grid grid-cols-1 gap-4 px-6 py-6 2xl:grid-cols-[200px_1fr] 2xl:gap-10" data-testid={testId}>
    <div className="flex items-start gap-1.5 2xl:pt-1">
      <h3 className="text-[13px] font-semibold text-slate-900">{title}</h3>
      {tip && <InfoTip text={tip} />}
    </div>
    <div className="min-w-0">{children}</div>
  </section>
);

export const FieldLabel = ({ children, required, optional, tip, htmlFor }) => (
  <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
    {children}{required && <span className="text-rose-500">*</span>}
    {optional && <span className="font-normal text-slate-400">Optional</span>}
    {tip && <InfoTip text={tip} />}
  </label>
);

export const StickyActionBar = ({ updatedAt, updatedBy, saving, dirty, onCancel, onSave, saveTestId, cancelTestId, barTestId }) => (
  <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur" data-testid={barTestId}>
    <span className="text-xs text-slate-400" data-testid={barTestId ? `${barTestId}-meta` : undefined}>
      {updatedAt ? <>Last saved {formatDateTimeLocal(updatedAt)}{updatedBy ? <span className="hidden sm:inline"> · {updatedBy}</span> : null}</> : null}
    </span>
    <div className="flex items-center gap-2">
      <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving || !dirty} data-testid={cancelTestId}>Cancel</Button>
      <Button type="button" size="sm" onClick={onSave} disabled={saving} data-testid={saveTestId} className="min-w-[128px]">
        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Changes
      </Button>
    </div>
  </div>
);
